import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { salonAPI, servicesAPI, clientsAPI, staffAPI, appointmentsAPI, accountingAPI } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
    Calendar as CalendarIcon, 
    Users, 
    Wallet, 
    Clock, 
    Plus,
    TrendingUp,
    CheckCircle2,
    XCircle,
    AlertCircle
} from "lucide-react";
import { formatCurrency, formatTime, getGreeting, getStatusColor } from "@/lib/utils";
import { toast } from "sonner";
import NewAppointmentDialog from "@/components/NewAppointmentDialog";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';

export default function DashboardPage() {
    const { user } = useAuth();
    const [stats, setStats] = useState({
        today_appointments: 0,
        today_completed: 0,
        today_pending: 0,
        week_total: 0,
        week_completed: 0,
        month_total: 0,
        month_revenue: 0,
        total_clients: 0,
    });
    const [todayAppointments, setTodayAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showNewAppt, setShowNewAppt] = useState(false);
    const [services, setServices] = useState([]);
    const [clients, setClients] = useState([]);
    const [staff, setStaff] = useState([]);
    const [weeklyChartData, setWeeklyChartData] = useState([]);
    const [lastRefresh, setLastRefresh] = useState(new Date());

    useEffect(() => {
        loadDashboard();
        
        // Auto-reload dashboard every 2 minutes (120000 ms)
        const intervalId = setInterval(() => {
            loadDashboard();
        }, 120000); // 2 minutes
        
        // Cleanup interval on component unmount
        return () => clearInterval(intervalId);
    }, []);

    const getWeekDateRange = () => {
        const today = new Date();
        const dayOfWeek = today.getDay();
        const monday = new Date(today);
        monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        
        return {
            start_date: monday.toISOString().split('T')[0],
            end_date: sunday.toISOString().split('T')[0]
        };
    };

    const loadDashboard = async () => {
        try {
            const { start_date, end_date } = getWeekDateRange();
            
            const [statsRes, apptsRes, servicesRes, clientsRes, staffRes, transactionsRes] = await Promise.all([
                salonAPI.getDashboard(),
                appointmentsAPI.getToday(),
                servicesAPI.getAll(),
                clientsAPI.getAll(),
                staffAPI.getAll(),
                accountingAPI.getTransactions({ start_date, end_date })
            ]);
            
            // Extract stats - handle { success: true, data: { today: {...}, week: {...}, month: {...}, clients: ... } }
            const statsData = statsRes.data?.data || statsRes.data?.stats || statsRes.data || {};
            
            
            setStats({
                today_appointments: statsData.today?.total || 0,
                today_completed: statsData.today?.completed || 0,
                today_pending: statsData.today?.pending || 0,
                week_total: statsData.week?.total || 0,
                week_completed: statsData.week?.completed || 0,
                month_total: statsData.month?.total || 0,
                month_revenue: statsData.month?.revenue || 0,
                total_clients: statsData.clients || 0,
            });
            
            // Extract appointments - handle { success: true, data: [...] }
            const apptsData = apptsRes.data?.data || apptsRes.data?.appointments || [];
            setTodayAppointments(Array.isArray(apptsData) ? apptsData : []);
            
            // Extract services, clients, staff
            const extractData = (response, key) => {
                const data = response?.data;
                if (!data) return [];
                // Check for data.data first (standard format)
                if (Array.isArray(data.data)) return data.data;
                // Check for data[key] (e.g., data.services, data.clients)
                if (Array.isArray(data[key])) return data[key];
                // Check if data itself is an array
                if (Array.isArray(data)) return data;
                return [];
            };
            
            setServices(extractData(servicesRes, 'services'));
            setClients(extractData(clientsRes, 'clients'));
            setStaff(extractData(staffRes, 'staff'));
            
            // Process transactions for weekly chart
            const transData = transactionsRes.data?.data || transactionsRes.data?.transactions || [];
            const transactions = Array.isArray(transData) ? transData : [];
            const chartData = buildWeeklyChartData(transactions, start_date);
            setWeeklyChartData(chartData);
            
        } catch (error) {
            console.error('Dashboard load error:', error);
            console.error('Error response:', error.response?.data);
            toast.error("Failed to load dashboard");
        } finally {
            setLoading(false);
            setLastRefresh(new Date());
        }
    };

    const buildWeeklyChartData = (transactions, weekStartDate) => {
        const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        const startDate = new Date(weekStartDate);
        
        // Initialize chart data with 0 for each day
        const chartData = days.map((day, index) => {
            const date = new Date(startDate);
            date.setDate(startDate.getDate() + index);
            return {
                day,
                date: date.toISOString().split('T')[0],
                income: 0,
                expenses: 0,
                net: 0
            };
        });
        
        // Aggregate transactions by day
        transactions.forEach(txn => {
            const txnDate = txn.date;
            const dayData = chartData.find(d => d.date === txnDate);
            
            if (dayData) {
                const amount = parseFloat(txn.amount) || 0;
                if (txn.type === 'income' || txn.type === 'booking_fee') {
                    dayData.income += amount;
                } else if (txn.type === 'expense') {
                    dayData.expenses += amount;
                }
                dayData.net = dayData.income - dayData.expenses;
            }
        });
        
        return chartData;
    };

    const handleStatusChange = async (apptId, newStatus) => {
        try {
            if (newStatus === 'completed') {
                await appointmentsAPI.complete(apptId);
            } else if (newStatus === 'cancelled') {
                await appointmentsAPI.cancel(apptId);
            } else {
                await appointmentsAPI.update(apptId, { status: newStatus });
            }
            toast.success(`Appointment marked as ${newStatus}`);
            loadDashboard();
        } catch (error) {
            toast.error("Failed to update appointment");
        }
    };

    // Calculate weekly totals from chart data
    const weeklyIncome = weeklyChartData.reduce((sum, d) => sum + d.income, 0);
    const weeklyExpenses = weeklyChartData.reduce((sum, d) => sum + d.expenses, 0);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full min-h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 lg:p-12 space-y-8">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <p className="text-muted-foreground text-sm">{getGreeting()}</p>
                    <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">
                        <span className="font-accent">{user?.owner_name?.split(' ')[0] || user?.business_name}</span>
                    </h1>
                    <p className="text-xs text-muted-foreground mt-1">
                        Last updated: {lastRefresh.toLocaleTimeString('en-GH', { hour: '2-digit', minute: '2-digit' })}
                        {' • '}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <Button 
                        variant="outline"
                        onClick={() => {
                            toast.info("Refreshing dashboard...");
                            loadDashboard();
                        }}
                        className="rounded-xl"
                    >
                        <svg 
                            className="w-4 h-4 mr-2" 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                        >
                            <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                strokeWidth={2} 
                                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                            />
                        </svg>
                        Refresh
                    </Button>
                    <Button 
                        onClick={() => setShowNewAppt(true)}
                        className="rounded-xl px-6 text-white font-semibold"
                        style={{ 
                            background: 'linear-gradient(135deg, #00D4FF 0%, #12006d 100%)',
                            boxShadow: '0 4px 12px rgba(0, 212, 255, 0.3)'
                        }}
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        New Appointment
                    </Button>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="border-border/40 shadow-card hover:shadow-card-hover transition-shadow">
                    <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Today's Appointments</p>
                                <p className="text-3xl font-bold font-heading mt-2">
                                    {stats.today_appointments}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {stats.today_completed} completed, {stats.today_pending} pending
                                </p>
                            </div>
                            <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center">
                                <CalendarIcon className="w-5 h-5 text-accent-foreground" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-border/40 shadow-card hover:shadow-card-hover transition-shadow">
                    <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Weekly Income</p>
                                <p className="text-3xl font-bold font-accent mt-2 text-green-600">
                                    {formatCurrency(weeklyIncome)}
                                </p>
                                {weeklyExpenses > 0 && (
                                    <p className="text-xs text-red-500 mt-1">
                                        -{formatCurrency(weeklyExpenses)} expenses
                                    </p>
                                )}
                            </div>
                            <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                                <Wallet className="w-5 h-5 text-green-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-border/40 shadow-card hover:shadow-card-hover transition-shadow">
                    <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Total Clients</p>
                                <p className="text-3xl font-bold font-heading mt-2">
                                    {stats.total_clients}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {clients.length} in database
                                </p>
                            </div>
                            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                                <Users className="w-5 h-5 text-blue-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-border/40 shadow-card hover:shadow-card-hover transition-shadow">
                    <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">This Week</p>
                                <p className="text-3xl font-bold font-heading mt-2">
                                    {stats.week_total}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {stats.week_completed} completed
                                </p>
                            </div>
                            <div className="w-10 h-10 rounded-xl bg-yellow-100 flex items-center justify-center">
                                <Clock className="w-5 h-5 text-yellow-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Main Content Grid */}
            <div className="grid lg:grid-cols-3 gap-6">
                {/* Today's Schedule */}
                <Card className="lg:col-span-2 border-border/40 shadow-card">
                    <CardHeader className="flex flex-row items-center justify-between pb-4">
                        <div>
                            <CardTitle className="font-heading text-xl">Today's Schedule</CardTitle>
                            <CardDescription>
                                {new Date().toLocaleDateString('en-GH', { weekday: 'long', month: 'long', day: 'numeric' })}
                            </CardDescription>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {todayAppointments.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">
                                <CalendarIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <p>No appointments scheduled for today</p>
                                <Button 
                                    variant="link" 
                                    className="mt-2 text-primary"
                                    onClick={() => setShowNewAppt(true)}
                                >
                                    Book your first appointment
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-3 max-h-[400px] overflow-y-auto">
                                {todayAppointments.map((appt) => (
                                    <div 
                                        key={appt.id}
                                        className="flex items-center gap-4 p-4 rounded-xl bg-secondary/50 hover:bg-secondary transition-colors"
                                    >
                                        <div className="flex-shrink-0 w-16 text-center">
                                            <p className="text-lg font-semibold">{formatTime(appt.time)}</p>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium truncate">
                                                {appt.client?.name || appt.client_name || 'Unknown Client'}
                                            </p>
                                            <p className="text-sm text-muted-foreground">
                                                {appt.service?.name || appt.service_name || 'Unknown Service'}
                                            </p>
                                            {(appt.staff?.name || appt.staff_name) && (
                                                <p className="text-xs text-muted-foreground">
                                                    with {appt.staff?.name || appt.staff_name}
                                                </p>
                                            )}
                                        </div>
                                        <Badge className={getStatusColor(appt.status)}>
                                            {appt.status?.replace('_', ' ')}
                                        </Badge>
                                        {(appt.status === 'scheduled' || appt.status === 'confirmed') && (
                                            <div className="flex gap-1">
                                                <Button 
                                                    size="icon" 
                                                    variant="ghost" 
                                                    className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-100"
                                                    onClick={() => handleStatusChange(appt.id, 'completed')}
                                                    title="Mark as completed"
                                                >
                                                    <CheckCircle2 className="w-4 h-4" />
                                                </Button>
                                                <Button 
                                                    size="icon" 
                                                    variant="ghost" 
                                                    className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-100"
                                                    onClick={() => handleStatusChange(appt.id, 'cancelled')}
                                                    title="Cancel appointment"
                                                >
                                                    <XCircle className="w-4 h-4" />
                                                </Button>
                                                <Button 
                                                    size="icon" 
                                                    variant="ghost" 
                                                    className="h-8 w-8 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-100"
                                                    onClick={() => handleStatusChange(appt.id, 'no_show')}
                                                    title="Mark as no-show"
                                                >
                                                    <AlertCircle className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Weekly Revenue Chart */}
                <Card className="border-border/40 shadow-card">
                    <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                            <CardTitle className="font-heading text-lg">Weekly Revenue</CardTitle>
                            <TrendingUp className="w-4 h-4 text-green-500" />
                        </div>
                        <p className="text-2xl font-accent font-bold text-primary">
                            {formatCurrency(weeklyIncome)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                            Net: {formatCurrency(weeklyIncome - weeklyExpenses)}
                        </p>
                    </CardHeader>
                    <CardContent>
                        <div className="h-[220px] mt-4">
                            {weeklyChartData.every(d => d.income === 0 && d.expenses === 0) ? (
                                <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                                    <Wallet className="w-10 h-10 mb-2 opacity-50" />
                                    <p className="text-sm">No transactions this week</p>
                                </div>
                            ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={weeklyChartData}>
                                        <XAxis 
                                            dataKey="day" 
                                            axisLine={false} 
                                            tickLine={false}
                                            tick={{ fontSize: 12, fill: '#78716C' }}
                                        />
                                        <YAxis 
                                            hide 
                                            domain={[0, 'auto']}
                                        />
                                        <Tooltip 
                                            formatter={(value, name) => [
                                                formatCurrency(value), 
                                                name === 'income' ? 'Income' : 'Expenses'
                                            ]}
                                            labelFormatter={(label, payload) => {
                                                if (payload && payload[0]) {
                                                    return `${label} (${payload[0].payload.date})`;
                                                }
                                                return label;
                                            }}
                                            contentStyle={{ 
                                                borderRadius: '12px', 
                                                border: 'none',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                            }}
                                        />
                                        <Bar 
                                            dataKey="income" 
                                            fill="#10B981" 
                                            radius={[4, 4, 0, 0]}
                                            name="income"
                                        />
                                        <Bar 
                                            dataKey="expenses" 
                                            fill="#EF4444" 
                                            radius={[4, 4, 0, 0]}
                                            name="expenses"
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Monthly Summary */}
            <Card className="border-border/40 shadow-card">
                <CardHeader>
                    <CardTitle className="font-heading text-xl">Monthly Overview</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        <div className="text-center p-4 rounded-xl bg-secondary/50">
                            <p className="text-sm text-muted-foreground mb-1">Monthly Revenue</p>
                            <p className="text-2xl font-accent font-bold text-primary">
                                {formatCurrency(stats.month_revenue)}
                            </p>
                        </div>
                        <div className="text-center p-4 rounded-xl bg-secondary/50">
                            <p className="text-sm text-muted-foreground mb-1">Monthly Appointments</p>
                            <p className="text-2xl font-bold">{stats.month_total}</p>
                        </div>
                        <div className="text-center p-4 rounded-xl bg-secondary/50">
                            <p className="text-sm text-muted-foreground mb-1">Services</p>
                            <p className="text-2xl font-bold">{services.length}</p>
                        </div>
                        <div className="text-center p-4 rounded-xl bg-secondary/50">
                            <p className="text-sm text-muted-foreground mb-1">Staff</p>
                            <p className="text-2xl font-bold">{staff.length}</p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* New Appointment Dialog */}
            <NewAppointmentDialog 
                open={showNewAppt} 
                onOpenChange={setShowNewAppt}
                clients={clients}
                services={services}
                staff={staff}
                onSuccess={loadDashboard}
            />
        </div>
    );
}